import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { Rocket, Phone, Email } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const colors = {
  cobaltDeep: '#1a2f6c',
  cobaltMain: '#2b4cc0',
  cobaltBright: '#5a7dff',
  cobaltSky: '#7d9fff',
  cobaltIce: '#b3c9ff',
  midnight: '#0f1a34',
  frost: '#f5f7ff',
};

const CTASection: React.FC = () => {
  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        background: `linear-gradient(165deg, ${colors.cobaltDeep} 0%, ${colors.cobaltMain} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${colors.cobaltBright}30 0%, transparent 70%)`,
          filter: 'blur(100px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          left: '-10%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${colors.cobaltSky}20 0%, transparent 70%)`,
          filter: 'blur(100px)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={5} alignItems="center" textAlign="center">
          {/* 메인 헤딩 */}
          <Stack spacing={2}>
            <Typography
              variant="h2"
              sx={{
                color: colors.frost,
                fontWeight: 700,
                fontSize: { xs: '2.2rem', md: '3.5rem' },
                lineHeight: 1.2,
              }}
            >
              지금 바로 시작하세요
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: colors.cobaltIce,
                fontWeight: 400,
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                maxWidth: '800px',
                lineHeight: 1.6,
              }}
            >
              데이터가 경쟁력이 되는 스마트한 미래,<br />
              EGMMS와 함께 만드십시오
            </Typography>
          </Stack>

          {/* CTA 버튼들 */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            <Button
              component={Link}
              to="/login"
              variant="contained"
              size="large"
              startIcon={<Rocket />}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '50px',
                background: colors.frost,
                color: colors.cobaltDeep,
                boxShadow: `0 15px 40px ${colors.cobaltBright}40`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: colors.cobaltIce,
                  transform: 'translateY(-3px)',
                  boxShadow: `0 20px 50px ${colors.cobaltBright}50`,
                },
              }}
            >
              무료 체험 시작
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<Phone />}
              href="tel:070-4248-9064"
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '50px',
                borderWidth: '2px',
                borderColor: colors.frost,
                color: colors.frost,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderWidth: '2px',
                  borderColor: colors.cobaltIce,
                  background: `${colors.frost}15`,
                  transform: 'translateY(-3px)',
                },
              }}
            >
              전문가 상담 신청
            </Button>
          </Stack>

          {/* 특별 혜택 표시 */}
          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: '20px',
              background: `${colors.frost}10`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${colors.cobaltIce}30`,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: colors.cobaltIce,
                fontSize: '1rem',
                '& strong': {
                  color: colors.frost,
                  fontWeight: 700,
                },
              }}
            >
              지금 문의하시면 특별한 혜택을 받으실 수 있습니다.
            </Typography>
          </Box>

          {/* 연락처 정보 */}
          <Box 
            id="contact"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', mt: 4 }}
          >
            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(50% - 16px)' } }}>
              <Stack alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${colors.frost}20`,
                  }}
                >
                  <Email sx={{ fontSize: 28, color: colors.frost }} />
                </Box>
                <Typography variant="subtitle2" sx={{ color: colors.cobaltIce }}>
                  이메일
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.frost, fontWeight: 600 }}
                >
                  sales@moldcare.kr
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(50% - 16px)' } }}>
              <Stack alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${colors.frost}20`,
                  }}
                >
                  <Phone sx={{ fontSize: 28, color: colors.frost }} />
                </Box>
                <Typography variant="subtitle2" sx={{ color: colors.cobaltIce }}>
                  전화
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.frost, fontWeight: 600 }}
                >
                  010-4063-9062
                </Typography>
              </Stack>
            </Box>
          </Box>

          {/* 보증 배지 */}
          <Stack
            direction="row"
            spacing={4}
            sx={{ mt: 5, flexWrap: 'wrap', justifyContent: 'center' }}
          >
            <Typography
              variant="body2"
              sx={{ color: colors.cobaltIce, fontSize: '0.9rem' }}
            >
              ✓ 무료 체험
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.cobaltIce, fontSize: '0.9rem' }}
            >
              ✓ 계약즉시 5일내 사용
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.cobaltIce, fontSize: '0.9rem' }}
            >
              ✓ 설치 불필요
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default CTASection;
